import { Box, Drawer } from '@material-ui/core';
import { useRoutes, useStore } from '@roc/client-portal-shared/hooks';
import { Layout } from '@roc/feature-app-core';
import {
  ConversationDrawer,
  useCommunicationRoutesConfig,
  useAppraisalCommunicationStore
} from '@roc/feature-communication';
import {
  AppraisalOrderDetailsDialog, AppraisalDetailsDialogV2,
  useDocumentCommunicationStore,
} from '@roc/feature-documents';
import { ThreadMeta } from '@roc/feature-types';
import { FilterButton, StandardDialog, Tabs } from '@roc/ui';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { LoanAppraisalsGrid } from './components/loanAppraisalsGrid';
import { MyChatContainer } from '@roc/feature-communication';
import { useLoanCommunicationStore } from '@roc/feature-communication';
import { SelectConversation } from '@roc/feature-communication';
import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { AppraisalChat } from '../chat';

const TABS = [
  {
    label: 'ACTIVE APPRAISALS',
  },
  {
    label: 'COMPLETED APPRAISALS',
  },
];

const APPRAISAL_ORDER_SECTION_ID = '20';
const SECOND_APPRAISAL_ORDER_SECTION_ID = '22';
const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      height: 'min(100%,1200px)',
    },
    '& .MuiDialogContent-root': {
      height: '100%',
    },
    '& button[aria-label="close"]': {
      top: '1px',
    },
  },
  drawer: {
    '& .MuiDrawer-paper': {
      [theme.breakpoints.up('md')]: {
        width: 1000,
      },
      [theme.breakpoints.up('lg')]: {
        width: 1200,
      },
    },
  },
  drawerContent: {
    [theme.breakpoints.up('md')]: {
      width: 1000,
    },
    [theme.breakpoints.up('lg')]: {
      width: 1200,
    },
  },
  chatContainer: {
    flex: 1,
  },
}));

export const BrowseAppraisals = observer(() => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [appraisalModalOpen, setAppraisalModalOpen] = useState(false);
  const [appraisalV2ModalOpen, setAppraisalV2ModalOpen] = useState(false);
  const [selectedPropertyAppraisal, setSelectedPropertyAppraisal] = useState(null);
  const { appraisalsStore, appraisalDetailsV2Store, userStore } = useStore();
  const { documentCommunicationStore } = useDocumentCommunicationStore();
  const { appraisalsRoutesConfig } = useRoutes();
  const { appraisalCommunicationStore } = useAppraisalCommunicationStore();
  const { push } = useHistory();
  const { loanCommunicationStore } = useLoanCommunicationStore();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [loanId, setLoanId] = useState(0);
  const [openStandaloneChat, setOpenStandaloneChat] = useState(false);
  const [appraisalId, setAppraisalId] = useState(0);
  const classes = useStyles();

  useEffect(() => {
    userStore.getUserInfoAndEntitlements();
  }, []);

  const onTabChange = (e, index) => setSelectedTab(index);

  const openAppraisalCommunication = async appraisal => {
    if (appraisal.loanId) {
      setLoanId(appraisal.loanId);
      setOpenDrawer(true);
    } else { // standalone
      setOpenStandaloneChat(true);
      setAppraisalId(appraisal.loanPropertyValuationId);
    }
  };

  const handleToggleFilter = () => {
    if (selectedTab === 0) {
      appraisalsStore.loanAppraisalsGridStore.toggleFilters();
    } else {
      appraisalsStore.standaloneAppraisalsGridStore.toggleFilters();
    }
  };

  const handleClearFilter = () => {
    if (selectedTab === 0) {
      appraisalsStore.loanAppraisalsGridStore.resetFilters();
    } else {
      appraisalsStore.standaloneAppraisalsGridStore.resetFilters();
    }
  };

  const handleSelectAppraisal = async data => {
    setSelectedPropertyAppraisal(data);
    if (data?.loanId) {
      await appraisalDetailsV2Store.loanStore.fetchLoanDetails(data?.loanId);
    }
    setAppraisalV2ModalOpen(true);
  };

  const closeAppraisalModal = () => {
    setAppraisalModalOpen(false);
  };

  const closeDrawer = () => {
    setOpenDrawer(false)
  }

  const closeStandaloneChat = () => {
    setAppraisalId(0);
    setOpenStandaloneChat(false);
  }

  const closeTwilioAppraisalCommunication = () => {
    push(appraisalsRoutesConfig.appraisals.children.browseAppraisals.url);
  };

  useEffect(() => {
    if (!documentCommunicationStore.isDrawerOpen) {
      if (selectedTab === 0) {
        appraisalsStore.setLoanAppraisalGridType('ACTIVE');
        appraisalsStore.loanAppraisalsGridStore.resetAndFetchGridData();
      } else if (selectedTab === 1) {
        appraisalsStore.setLoanAppraisalGridType('COMPLETED');
        appraisalsStore.loanAppraisalsGridStore.resetAndFetchGridData();
      } else {
        appraisalsStore.standaloneAppraisalsGridStore.resetAndFetchGridData();
      }
    }
  }, [documentCommunicationStore.isDrawerOpen, appraisalsStore, selectedTab]);

  return (
    <Layout title="Appraisals">
      <Tabs
        tabs={TABS}
        selectedTab={selectedTab}
        onTabChange={onTabChange}
        right={
          <Box py={1} pr={2}>
            <FilterButton
              onToggleFilters={handleToggleFilter}
              onClearFilters={handleClearFilter}
            />
          </Box>
        }
      />
      <LoanAppraisalsGrid
        onOpenCommunication={openAppraisalCommunication}
        onSelectAppraisal={handleSelectAppraisal}
      />
      <AppraisalOrderDetailsDialog
        open={appraisalModalOpen}
        onClose={closeAppraisalModal}
        propertyAppraisalId={selectedPropertyAppraisal?.valuationId}
        document={null}
      />
      <AppraisalDetailsDialogV2
        open={appraisalV2ModalOpen}
        onClose={() => setAppraisalV2ModalOpen(false)}
        loanId={selectedPropertyAppraisal?.loanId}
        loanPropertyValuationId={selectedPropertyAppraisal?.loanPropertyValuationId}
      />
      <ConversationDrawer
        communicationStore={appraisalCommunicationStore}
        onClose={closeTwilioAppraisalCommunication}
      />
      <Drawer
        className={classes.drawer}
        anchor="right"
        open={openDrawer}
        onClose={closeDrawer}
      >
        <Box display="flex" height="100%" >
          <MyChatContainer
            id={Number(loanId)}
            communicationStore={loanCommunicationStore}
            allowNewConversations={false}
            showSnippet={false}
            onConversationSelect={() => { }}
            className={classes.chatContainer}
          />
          <SelectConversation communicationStore={loanCommunicationStore} />
        </Box>
      </Drawer>
      {!!appraisalId && (
        <StandardDialog
          open={openStandaloneChat}
          onClose={closeStandaloneChat}
          handleClose={closeStandaloneChat}
          maxWidth={'lg'}
          title={'Standalone Appraisal'}
          dialogContent={
            <AppraisalChat
              store={appraisalCommunicationStore}
              appraisalId={appraisalId}
            />
          }
          removeContentBox={true}
          className={clsx(classes.dialog)}
        />
      )}
    </Layout>
  );
});
